import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useCallback, useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import { CategoryScale, Chart as ChartJS, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js'
import { convertHexToRgba } from '../../../../../utility/stylesUtility'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import chartTooltipClasses from '../ChartTooltip.module.scss'
import classes from './MarketingAnalyticsWidget.module.scss'
import globals from '../../../../../assets/styles/globals.scss'
import { ReactComponent as StatsUpIcon } from '../../../../../assets/images/common/icons/stats-up-icon.svg'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

Tooltip.positioners.cursor = function(elements, coordinates) {
  return { x: coordinates.x, y: coordinates.y }
}

const CHART_CONFIGS = {
  landingPageViews: {
    title: 'Landing Page Views',
    color: globals.primaryOrange,
  },
  leadsGenerated: {
    title: 'Leads Generated',
    color: globals.tertiaryBlueText,
  },
  registrations: {
    title: 'Registrations',
    color: globals.slateGray,
  },
}

const computeGrowthRate = (total, prevYearValue) => {
  if (prevYearValue === null || prevYearValue === undefined || prevYearValue === 0) {
    return null
  }
  return (total - prevYearValue) / prevYearValue
}

const formatGrowthRate = (rate) => {
  if (rate === null) {
    return 'N/A'
  }
  return Intl.NumberFormat('en-US', { style: 'percent', signDisplay: 'exceptZero' })
    .format(rate)
}

const MarketingAnalyticsLineChart = ({ chartKey, labels, data, statistics }) => {
  const config = CHART_CONFIGS[chartKey]
  const growthRate = computeGrowthRate(statistics?.value, statistics?.prevYearValue)

  const externalTooltipHandler = useCallback(context => {
    const tooltipId = `marketing-analytics-tooltip-${chartKey}`
    let tooltipEl = document.getElementById(tooltipId)

    if (!tooltipEl) {
      tooltipEl = document.createElement('div')
      tooltipEl.id = tooltipId
      tooltipEl.className = chartTooltipClasses.tooltip
      document.body.appendChild(tooltipEl)
    }

    const tooltipModel = context.tooltip

    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = 0
      return
    }

    const dataIndex = tooltipModel.dataPoints?.[0]?.dataIndex

    if (dataIndex === undefined) {
      tooltipEl.style.opacity = 0
      return
    }

    tooltipEl.innerHTML = `
      <div style="font-weight:800; font-size:1rem; color:${globals.darkNavy}; margin-bottom:0.5rem;">
        ${labels[dataIndex]}
      </div>
      <span style="color:${config.color}; white-space:nowrap;">
        ${config.title}: ${data[dataIndex]}
      </span>
    `

    const canvasRect = context.chart.canvas.getBoundingClientRect()
    tooltipEl.style.opacity = 1

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = canvasRect.left + window.pageXOffset + tooltipModel.caretX + 12
    let top = canvasRect.top + window.pageYOffset + tooltipModel.caretY + 12

    const tooltipRect = tooltipEl.getBoundingClientRect()
    if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width - 8
    }
    if (top + tooltipRect.height > viewportHeight) {
      top = viewportHeight - tooltipRect.height - 8
    }

    tooltipEl.style.left = left + 'px'
    tooltipEl.style.top = top + 'px'
  }, [chartKey, config.color, config.title, data, labels])

  const chartData = useMemo(() => ({
    labels,
    datasets: [{
      label: config.title,
      data,
      borderColor: config.color,
      backgroundColor: convertHexToRgba(config.color, 0.15),
      fill: true,
      tension: 0.35,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHoverBackgroundColor: config.color,
      pointHoverBorderColor: config.color,
      borderWidth: 2,
    }],
  }), [labels, data, config])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
        position: 'cursor',
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: globals.slateGray,
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 4,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          maxTicksLimit: 5,
          color: config.color,
        },
        grid: {
          color: globals.slateGrayIcons,
          borderDash: [2, 4],
          drawTicks: false,
        },
        border: {
          display: false,
        },
      },
    },
  }), [externalTooltipHandler, config.color])

  return (
    <div className={classnames('d-flex flex-column', businessDashboardClasses.sectionWrapper, classes.sectionWrapper)}>
      <h3 className={businessDashboardClasses.title}>{config.title}</h3>
      <div className={classnames('d-flex align-items-end', classes.chartStatisticsWrapper)}>
        <span className={classes.chartValue}>{statistics?.value ?? '-'}</span>
        <StatsUpIcon className={classnames('ml-2 mb-1', classes.icon)} />
        {growthRate !== null && growthRate !== 0 && (
          <span
            className={classnames(
              'ml-4 mb-1',
              classes.growthValue,
              { [classes.green]: growthRate > 0 },
              { [classes.red]: growthRate < 0 }
            )}
          >
            {formatGrowthRate(growthRate)}
          </span>
        )}
      </div>
      <div className='flex-grow-1 min-w-0 min-h-0'>
        <Line data={chartData} options={options} />
      </div>
      <div className='d-flex align-items-center'>
        <span className={classes.todayValue}>{statistics?.todayValue ?? '-'}</span>
        <span className={classnames('ml-2', classes.todayLabel)}>Today</span>
      </div>
    </div>
  )
}

MarketingAnalyticsLineChart.propTypes = {
  chartKey: PropTypes.oneOf(['landingPageViews', 'leadsGenerated', 'registrations']).isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.arrayOf(PropTypes.number).isRequired,
  statistics: PropTypes.shape({
    value: PropTypes.number,
    todayValue: PropTypes.number,
    prevYearValue: PropTypes.number,
  }),
}

export default MarketingAnalyticsLineChart
